import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SourceIcon from '@mui/icons-material/Source';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PinterestIcon from '@mui/icons-material/Pinterest';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RedditIcon from '@mui/icons-material/Reddit';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/Share';
import { updateLead } from '../../Action/api';

const countryOptions = [
  { value: 'us', label: 'United States' },
  { value: 'in', label: 'India' },
  { value: 'au', label: 'Australia' },
  { value: 'ca', label: 'Canada' },
  { value: 'de', label: 'Germany' },
  { value: 'uk', label: 'United Kingdom' },
];

const planOptions = [
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

const companySizeOptions = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' },
];

const sourceOptions = [
  { value: 'Website', label: 'Website' },
  { value: 'Landing Page', label: 'Landing Page' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Referral', label: 'Referral' },
  { value: 'Cold Call', label: 'Cold Call' },
  { value: 'Trade Show', label: 'Trade Show' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Email Campaign', label: 'Email Campaign' },
  { value: 'Partner Referral', label: 'Partner Referral' },
];

const requestTypeOptions = [
  { value: 'Demo Request', label: 'Demo Request' },
  { value: 'Pricing Inquiry', label: 'Pricing Inquiry' },
  { value: 'Support Request', label: 'Support Request' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Callback Request', label: 'Callback Request' },
  { value: 'General Inquiry', label: 'General Inquiry' },
];

const bestTimeOptions = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
];

const statusOptions = [
  { value: 'New', label: 'New' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Disqualified', label: 'Disqualified' },
  { value: 'Closed Won', label: 'Closed Won' },
  { value: 'Closed Loss', label: 'Closed Loss' },
];

const leadTypeOptions = [
  { value: 'Lead', label: 'Lead' },
  { value: 'Prospect', label: 'Prospect' },
  { value: 'Customer', label: 'Customer' },
];

const industryOptions = [
  { value: 'Technology', label: 'Technology' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Education', label: 'Education' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Hospitality', label: 'Hospitality' },
  { value: 'Transportation', label: 'Transportation' },
  { value: 'Energy', label: 'Energy' },
  { value: 'Telecommunications', label: 'Telecommunications' },
  { value: 'Media & Entertainment', label: 'Media & Entertainment' },
  { value: 'Consulting', label: 'Consulting' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Automotive', label: 'Automotive' },
  { value: 'Agriculture', label: 'Agriculture' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Pharmaceuticals', label: 'Pharmaceuticals' },
  { value: 'E-commerce', label: 'E-commerce' },
  { value: 'Non-Profit', label: 'Non-Profit' },
  { value: 'Government', label: 'Government' },
  { value: 'Marketing & Advertising', label: 'Marketing & Advertising' },
  { value: 'Food & Beverage', label: 'Food & Beverage' },
  { value: 'Others', label: 'Others' },
];

const LeadDetailsModal = ({ open, lead, onClose, onUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [showSocialMedia, setShowSocialMedia] = useState(false);

  useEffect(() => {
    if (lead && open) {
      setForm({
        fullName: lead.fullName || '',
        email: lead.email || '',
        phoneNumber: lead.phoneNumber || '',
        companyName: lead.companyName || '',
        companyWebsite: lead.companyWebsite || '',
        jobTitle: lead.jobTitle || '',
        country: (lead.country || '').toLowerCase(),
        companySize: lead.companySize || '',
        industry: lead.industry || '',
        planType: lead.planType || '',
        source: lead.source || '',
        requestType: lead.requestType || '',
        numberOfEmployees: lead.numberOfEmployees || '',
        bestTimeToConnect: lead.bestTimeToConnect || '',
        reasonForRequest: lead.reasonForRequest || '',
        location: lead.location || '',
        facebookUrl: lead.facebookUrl || '',
        instagramUrl: lead.instagramUrl || '',
        linkedinUrl: lead.linkedinUrl || '',
        twitterUrl: lead.twitterUrl || '',
        youtubeUrl: lead.youtubeUrl || '',
        tiktokUrl: lead.tiktokUrl || '',
        pinterestUrl: lead.pinterestUrl || '',
        whatsappUrl: lead.whatsappUrl || '',
        snapchatUrl: lead.snapchatUrl || '',
        redditUrl: lead.redditUrl || '',
        otherSocialUrl: lead.otherSocialUrl || '',
        leadType: lead.leadType || 'Lead',
        status: lead.status || 'New',
      });
      setError('');
      setSubmitting(false);
      setIsEditing(false);
      setTouched({});
      
      // Check if any social media field has data to auto-show section
      const hasSocialMedia = lead.facebookUrl || lead.instagramUrl || lead.linkedinUrl || 
        lead.twitterUrl || lead.youtubeUrl || lead.tiktokUrl || lead.pinterestUrl || 
        lead.whatsappUrl || lead.snapchatUrl || lead.redditUrl || lead.otherSocialUrl;
      setShowSocialMedia(!!hasSocialMedia);
    }
  }, [lead, open]);

  if (!lead) return null;

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const requiredEditFields = ['fullName', 'email', 'phoneNumber', 'source', 'requestType'];
  
  const getFieldError = (fieldName) => {
    return touched[fieldName] && !form[fieldName];
  };

  const handleSave = async () => {
    // Mark required fields as touched
    const allTouched = requiredEditFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (!form.fullName || !form.email || !form.phoneNumber || !form.source || !form.requestType) {
      setError('⚠️ Please fill all required fields marked with red borders.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await updateLead({
        ...lead,
        ...form,
        leadId: lead.leadId,
        leadType: form.status,
      });
      setIsEditing(false);
      onUpdated?.();
    } catch (saveError) {
      setError(saveError?.message || 'Failed to update lead details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={{ xs: 'sm', sm: 'md', md: 'lg' }}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 1, sm: 2, md: 3 },
          overflow: 'hidden',
          maxHeight: '94vh',
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.16)',
          border: '1px solid rgba(75, 90, 182, 0.12)',
          background: '#fff',
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 1.5, sm: 2, md: 2.5 }, pb: { xs: 0.75, sm: 1, md: 1.25 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {isEditing ? 'Edit Lead' : 'Lead Details'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Lead ID: {lead.leadId}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          pt: 2,
          pb: 1.5,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          maxHeight: { xs: '66vh', sm: '68vh', md: '72vh' },
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Full Name" name="fullName" value={form.fullName || ''} onChange={handleChange} onBlur={() => handleBlur('fullName')} error={getFieldError('fullName')} helperText={getFieldError('fullName') ? 'Full Name is required' : ''} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><AssignmentIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Email" name="email" value={form.email || ''} onChange={handleChange} onBlur={() => handleBlur('email')} error={getFieldError('email')} helperText={getFieldError('email') ? 'Email is required' : ''} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Phone Number" name="phoneNumber" value={form.phoneNumber || ''} onChange={handleChange} onBlur={() => handleBlur('phoneNumber')} error={getFieldError('phoneNumber')} helperText={getFieldError('phoneNumber') ? 'Phone is required' : ''} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Job Title" name="jobTitle" value={form.jobTitle || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><BusinessCenterIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Company Name" name="companyName" value={form.companyName || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><BusinessCenterIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Company Size" name="companySize" value={form.companySize || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><WorkspacePremiumIcon fontSize="small" /></InputAdornment>) }}>
              {companySizeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Company Website" name="companyWebsite" type="url" value={form.companyWebsite || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://example.com"
              InputProps={{ startAdornment: (<InputAdornment position="start"><LanguageIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Industry" name="industry" value={form.industry || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><CategoryIcon fontSize="small" /></InputAdornment>) }}>
              {industryOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Country" name="country" value={form.country || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><PublicIcon fontSize="small" /></InputAdornment>) }}>
              {countryOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Plan Type" name="planType" value={form.planType || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><WorkspacePremiumIcon fontSize="small" /></InputAdornment>) }}>
              {planOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Lead Type" name="leadType" value={form.leadType || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SourceIcon fontSize="small" /></InputAdornment>) }}>
              {leadTypeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Lead Source" name="source" value={form.source || ''} onChange={handleChange} onBlur={() => handleBlur('source')} error={getFieldError('source')} helperText={getFieldError('source') ? 'Source is required' : ''} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SourceIcon fontSize="small" /></InputAdornment>) }}>
              {sourceOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Request Type" name="requestType" value={form.requestType || ''} onChange={handleChange} onBlur={() => handleBlur('requestType')} error={getFieldError('requestType')} helperText={getFieldError('requestType') ? 'Request Type is required' : ''} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><AssignmentIcon fontSize="small" /></InputAdornment>) }}>
              {requestTypeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Status" name="status" value={form.status || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><AssignmentIcon fontSize="small" /></InputAdornment>) }}>
              {statusOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Number of Employees" name="numberOfEmployees" value={form.numberOfEmployees || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><BusinessCenterIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Best Time to Connect" name="bestTimeToConnect" value={form.bestTimeToConnect || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing}
              InputProps={{ startAdornment: (<InputAdornment position="start"><ScheduleIcon fontSize="small" /></InputAdornment>) }}>
              {bestTimeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason for Request"
              name="reasonForRequest"
              value={form.reasonForRequest || ''}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={4}
              size="small"
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <DescriptionIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText sx={{ ml: 0.5, mt: 1 }}>
              Submitted: {lead.submittedAt ? new Date(lead.submittedAt).toLocaleString('en-US', {
                year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
              }) : 'N/A'}
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Location/Address"
              name="location"
              value={form.location || ''}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
              size="small"
              disabled={!isEditing}
              placeholder="Enter the company address or location"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Social Media Presence Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#4B5AB6', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon /> Social Media Presence
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSocialMedia}
                    onChange={(e) => setShowSocialMedia(e.target.checked)}
                    disabled={!isEditing}
                    size="small"
                  />
                }
                label="Show Social Media"
                sx={{ m: 0 }}
              />
            </Box>
          </Grid>
          
          {showSocialMedia && (
            <>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Facebook" name="facebookUrl" type="url" value={form.facebookUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://facebook.com/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Instagram" name="instagramUrl" type="url" value={form.instagramUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://instagram.com/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="LinkedIn" name="linkedinUrl" type="url" value={form.linkedinUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://linkedin.com/company/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Twitter / X" name="twitterUrl" type="url" value={form.twitterUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://twitter.com/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><TwitterIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="YouTube" name="youtubeUrl" type="url" value={form.youtubeUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://youtube.com/@yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><YouTubeIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="TikTok" name="tiktokUrl" type="url" value={form.tiktokUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://tiktok.com/@yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Pinterest" name="pinterestUrl" type="url" value={form.pinterestUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://pinterest.com/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><PinterestIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="WhatsApp Business" name="whatsappUrl" type="url" value={form.whatsappUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://wa.me/1234567890"
              InputProps={{ startAdornment: (<InputAdornment position="start"><WhatsAppIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Snapchat" name="snapchatUrl" type="url" value={form.snapchatUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://snapchat.com/add/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Reddit" name="redditUrl" type="url" value={form.redditUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="https://reddit.com/r/yourcompany"
              InputProps={{ startAdornment: (<InputAdornment position="start"><RedditIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Other Social Media" name="otherSocialUrl" type="url" value={form.otherSocialUrl || ''} onChange={handleChange} fullWidth size="small" disabled={!isEditing} placeholder="Any other social media profile"
              InputProps={{ startAdornment: (<InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1, sm: 1.25, md: 1.5 },
          borderTop: '1px solid rgba(148, 163, 184, 0.18)',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          gap: { xs: 0.5, sm: 1 },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        {isEditing ? (
          <>
            <Button 
              onClick={() => setIsEditing(false)} 
              disabled={submitting} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: 700, 
                color: '#475569', 
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }, 
                flex: { xs: 1, sm: 'auto' },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                minWidth: { xs: 'auto', sm: 100, md: 120 },
                maxWidth: { xs: '100%', sm: 'auto', md: '150px' }
              }}
            >
              Cancel Edit
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={submitting}
              sx={{
                minWidth: { xs: 'auto', sm: 120, md: 160 },
                maxWidth: { xs: '100%', sm: 'auto', md: '180px' },
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4B5AB6 0%, #6374d9 100%)',
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                px: { xs: 1.5, sm: 2, md: 3 },
                flex: { xs: 1, sm: 'auto' },
              }}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setIsEditing(true)}
            variant="contained"
            sx={{
              minWidth: { xs: 'auto', sm: 100, md: 140 },
              maxWidth: { xs: '100%', sm: 'auto', md: '160px' },
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4B5AB6 0%, #6374d9 100%)',
              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 1.5, sm: 2, md: 3 },
              flex: { xs: 1, sm: 'auto' },
            }}
          >
            Edit
          </Button>
        )}
        <Button 
          onClick={onClose} 
          sx={{ 
            textTransform: 'none', 
            fontWeight: 700, 
            color: '#475569', 
            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }, 
            flex: { xs: 1, sm: 'auto' },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            px: { xs: 1.5, sm: 2, md: 2.5 },
            minWidth: { xs: 'auto', sm: 90, md: 110 },
            maxWidth: { xs: '100%', sm: 'auto', md: '140px' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadDetailsModal;